import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Search, MapPin, Phone, Clock, Globe, Users, Book, Filter, Eye, UserCog, LogOut, Shield, Map, List, Info, X } from 'lucide-react';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import ForcePasswordChange from './ForcePasswordChange';
import LibraryDashboard from './LibraryDashboard';
import AdminDashboard from './AdminDashboard';
import MapView from './MapView';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper: validate if a string is a proper external URL
const isValidUrl = (str) => {
  if (!str || typeof str !== 'string') return false;
  const trimmed = str.trim();
  if (!trimmed || trimmed.toUpperCase() === 'N/A' || trimmed.toUpperCase() === 'NO' || trimmed.toUpperCase() === 'NO TENEMOS') return false;
  // Must start with http(s):// or www.
  return /^https?:\/\//i.test(trimmed) || /^www\./i.test(trimmed);
};

// Helper: ensure URL has scheme
const ensureScheme = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('www.')) return 'https://' + url;
  return url;
};

// Helper: clean display text (trim trailing commas, extra whitespace)
const cleanDisplay = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/,\s*$/, '').trim();
};

// Helper: check if a value is meaningful (not N/A, empty, etc.)
const hasValue = (val) => {
  if (!val) return false;
  if (typeof val !== 'string') return true;
  const upper = val.trim().toUpperCase();
  return upper !== '' && upper !== 'N/A' && upper !== 'NA' && upper !== 'NO' && upper !== 'NONE' && upper !== 'NO TENEMOS';
};

// Debounce hook for real-time search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Main App Component with CRM Integration
const MainApp = () => {
  const [libraries, setLibraries] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [libraryTypes, setLibraryTypes] = useState([]);
  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'map', 'login', 'dashboard', 'admin', 'about'
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    municipality: '',
    library_type: ''
  });
  const [mapLibraries, setMapLibraries] = useState([]);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);

  const { user, logout, isAuthenticated, mustChangePassword } = useAuth();
  
  // Debounce search input for real-time search (300ms delay)
  const debouncedSearch = useDebounce(searchInput, 300);
  
  // Handle opening modal - save scroll position and lock body scroll
  const openModal = useCallback(async (library) => {
    setSavedScrollPosition(window.scrollY);
    // Show modal immediately with the cached copy so it feels instant
    setSelectedLibrary(library);
    setShowModal(true);
    // Push a history state so back button closes modal instead of leaving site
    window.history.pushState({ modal: true, libraryId: library.id }, '');
    // Lock body scroll without changing position
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '15px'; // Prevent layout shift from scrollbar

    // Fetch the freshest copy from MongoDB so any direct DB edit is reflected
    if (library.id) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/libraries/${library.id}`, { cache: 'no-store' });
        if (res.ok) {
          const fresh = await res.json();
          setSelectedLibrary(fresh);
          // Refresh the entry in the main list too so subsequent renders match
          setLibraries(prev => prev.map(l => (l.id === fresh.id ? fresh : l)));
        }
      } catch (e) {
        console.warn('Could not refresh library detail from API, showing cached copy', e);
      }
    }
  }, []);
  
  // Handle closing modal - restore scroll position
  const closeModal = useCallback(() => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    setShowModal(false);
    setSelectedLibrary(null);
  }, []);

  // Handle browser back button - close modal instead of leaving site
  useEffect(() => {
    const handlePopState = (event) => {
      if (showModal) {
        // Close the modal when back is pressed
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        setShowModal(false);
        setSelectedLibrary(null);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showModal]);

  // Auto-navigate based on auth state
  useEffect(() => {
    if (isAuthenticated && viewMode === 'login') {
      if (mustChangePassword) {
        setViewMode('change-password');
      } else if (user?.is_admin) {
        setViewMode('admin');
      } else {
        setViewMode('dashboard');
      }
    }
  }, [isAuthenticated, viewMode, mustChangePassword, user]);

  useEffect(() => {
    fetchLibraries();
    fetchMunicipalities();
    fetchLibraryTypes();
  }, []);

  // Load map libraries after main libraries are loaded
  useEffect(() => {
    if (libraries.length > 0) {
      loadMapLibraries();
    }
  }, [libraries]);

  useEffect(() => {
    fetchLibraries();
  }, [filters.municipality, filters.library_type, debouncedSearch]);

  const fetchLibraries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Add search parameter - use debouncedSearch directly
      if (debouncedSearch && debouncedSearch.trim()) {
        params.append('search', debouncedSearch.trim());
      }
      
      // Add municipality filter
      if (filters.municipality && filters.municipality.trim()) {
        params.append('municipality', filters.municipality.trim());
      }
      
      // Add library type filter
      if (filters.library_type && filters.library_type.trim()) {
        params.append('library_type', filters.library_type.trim());
      }
      
      const queryString = params.toString();
      const url = `${BACKEND_URL}/api/libraries${queryString ? `?${queryString}` : ''}`;
      
      console.log('Fetching libraries with URL:', url);
      
      const response = await fetch(url, { cache: 'no-store' });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`Fetched ${data.length} libraries`);
      
      // Sort libraries alphabetically by official name
      const sortedData = data.sort((a, b) => {
        const nameA = (a.official_name || '').toLowerCase();
        const nameB = (b.official_name || '').toLowerCase();
        return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
      });
      
      setLibraries(sortedData);
    } catch (error) {
      console.error('Error fetching libraries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMunicipalities = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/municipalities`);
      const data = await response.json();
      setMunicipalities(data.municipalities);
    } catch (error) {
      console.error('Error fetching municipalities:', error);
    }
  };

  const fetchLibraryTypes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/library-types`);
      const data = await response.json();
      setLibraryTypes(data.library_types);
    } catch (error) {
      console.error('Error fetching library types:', error);
    }
  };

  const loadMapLibraries = async () => {
    try {
      // Use libraries directly from database (already have plus_code and location_link)
      console.log(`Using ${libraries.length} libraries from database for map`);
      
      // All libraries from database already have the necessary location data
      // plus_code (from column J) and location_link (from column I)
      setMapLibraries(libraries);
    } catch (error) {
      console.error('Error loading map libraries:', error);
      setMapLibraries(libraries);
    }
  };

  const getFilteredMapLibraries = () => {
    const sourceLibraries = mapLibraries.length > 0 ? mapLibraries : libraries;
    
    // Apply the same filters to map libraries as we do to regular libraries
    const filtered = sourceLibraries.filter(library => {
      // Apply search filter - filter by official name AND community name with word boundary
      if (debouncedSearch && debouncedSearch.trim()) {
        const searchLower = debouncedSearch.trim().toLowerCase();
        const officialName = (library.official_name || '').toLowerCase();
        const communityName = (library.community_name || '').toLowerCase();
        
        // Word boundary matching: search term must appear at the start of a word
        const wordBoundaryRegex = new RegExp('\\b' + searchLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
        
        if (!wordBoundaryRegex.test(officialName) && !wordBoundaryRegex.test(communityName)) {
          return false;
        }
      }

      // Apply municipality filter
      if (filters.municipality && filters.municipality.trim()) {
        if (library.municipality !== filters.municipality) {
          return false;
        }
      }

      // Apply library type filter
      if (filters.library_type && filters.library_type.trim()) {
        if (library.library_type !== filters.library_type) {
          return false;
        }
      }

      return true;
    });
    
    return filtered;
  };

  const LibraryCard = ({ library }) => (
    <div className="library-card" data-testid={`library-card-${library.id}`}>
      <div className="library-header">
        <h3 className="library-name">{library.official_name}</h3>
        {library.community_name && library.community_name !== library.official_name && (
          <p className="community-name">"{library.community_name}"</p>
        )}
        <div className="library-badges">
          <span className="library-type">{library.library_type}</span>
          {library.status && (
            <span className={`status-badge ${library.status?.toLowerCase() === 'activa' ? 'status-active' : 'status-inactive'}`}>
              {library.status}
            </span>
          )}
        </div>
      </div>
      
      <div className="library-info">
        <div className="info-item">
          <MapPin className="icon" />
          <span>{library.municipality}</span>
        </div>
        
        {hasValue(library.phone) && (
          <div className="info-item">
            <Phone className="icon" />
            <span>{library.phone}</span>
          </div>
        )}
        
        {library.website && isValidUrl(library.website) && (
          <div className="info-item">
            <Globe className="icon" />
            <a href={ensureScheme(library.website)} target="_blank" rel="noopener noreferrer">
              Sitio Web
            </a>
          </div>
        )}

        {library.location_link && isValidUrl(library.location_link) && (
          <div className="info-item">
            <MapPin className="icon" />
            <a href={ensureScheme(library.location_link)} target="_blank" rel="noopener noreferrer">
              Ver Ubicación
            </a>
          </div>
        )}
      </div>
      
      <div className="library-actions-bottom">
        <button 
          className="btn-view-details"
          onClick={() => openModal(library)}
          data-testid={`view-details-${library.id}`}
        >
          <Eye className="icon" />
          Ver Detalles
        </button>
      </div>
    </div>
  );

  const LibraryDetailModal = ({ library, onClose }) => {
    const modalRef = useRef(null);
    
    // Handle ESC key to close modal
    useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };
      
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [onClose]);
    
    // Handle click outside modal to close
    const handleOverlayClick = (event) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    };
    
    const dayNames = {
      monday: 'Lunes',
      tuesday: 'Martes',
      wednesday: 'Miércoles',
      thursday: 'Jueves',
      friday: 'Viernes',
      saturday: 'Sábado',
      sunday: 'Domingo'
    };

    // Function to convert 24h format to 12h format with AM/PM
    const formatTime12h = (timeStr) => {
      if (!timeStr) return '';
      
      // If already in 12h format with AM/PM, return as is (after cleanup)
      const timeUpper = timeStr.toString().toUpperCase().trim();
      if (timeUpper.includes('AM') || timeUpper.includes('PM')) {
        // Clean up any double AM/PM or extra spaces
        return timeStr.replace(/\s+/g, ' ').trim();
      }
      
      // Try to parse as 24h format
      const timeParts = timeStr.split(':');
      if (timeParts.length < 2) return timeStr;
      
      let hour = parseInt(timeParts[0], 10);
      const minute = timeParts[1];
      
      if (isNaN(hour)) return timeStr;
      
      const period = hour >= 12 ? 'PM' : 'AM';
      
      // Convert hour to 12-hour format
      if (hour === 0) {
        hour = 12; // Midnight
      } else if (hour > 12) {
        hour = hour - 12;
      }
      
      return `${hour}:${minute} ${period}`;
    };

    return (
    <div className="modal-overlay" onClick={handleOverlayClick} data-testid="modal-overlay">
      <div className="modal-container" ref={modalRef} data-testid="library-detail-modal">
        <div className="modal-header">
          <div className="modal-title-section">
            <h1 className="modal-title">{library.official_name}</h1>
            {library.community_name && library.community_name !== library.official_name && (
              <p className="modal-subtitle">Conocida como: "{library.community_name}"</p>
            )}
          </div>
          <button 
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="modal-close-btn"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="modal-content">
          <div className="detail-grid">
        <div className="detail-section">
          <h3>📋 Información Básica</h3>
          <div className="detail-item">
            <strong>Tipo:</strong> {library.library_type}
          </div>
          <div className="detail-item">
            <strong>Municipio:</strong> {library.municipality}
          </div>
          {library.status && (
            <div className="detail-item">
              <strong>Estatus:</strong> 
              <span className={`status-badge ${library.status?.toLowerCase() === 'activa' ? 'status-active' : 'status-inactive'}`}>
                {library.status}
              </span>
            </div>
          )}
          {hasValue(library.head_librarian) && (
            <div className="detail-item">
              <strong>Bibliotecario/a a Cargo:</strong> {library.head_librarian}
            </div>
          )}
          {hasValue(library.opening_date) && (
            <div className="detail-item">
              <strong>Fecha de Apertura:</strong> {library.opening_date}
            </div>
          )}
        </div>

        <div className="detail-section">
          <h3>📞 Contacto</h3>
          {hasValue(library.email) && (
            <div className="detail-item">
              <strong>Email:</strong> <a href={`mailto:${library.email}`}>{library.email}</a>
            </div>
          )}
          {hasValue(library.phone) && (
            <div className="detail-item">
              <strong>Teléfono:</strong> {library.phone}
            </div>
          )}
          {library.website && isValidUrl(library.website) && (
            <div className="detail-item">
              <strong>Sitio Web:</strong> <a href={ensureScheme(library.website)} target="_blank" rel="noopener noreferrer">{library.website}</a>
            </div>
          )}
        </div>

        <div className="detail-section">
          <h3>📍 Ubicación</h3>
          {hasValue(library.physical_address) && (
            <div className="detail-item">
              <strong>Dirección:</strong> {library.physical_address}
            </div>
          )}
          {hasValue(library.postal_address) && (
            <div className="detail-item">
              <strong>Dirección Postal:</strong> {library.postal_address}
            </div>
          )}
          {library.location_link && isValidUrl(library.location_link) && (
            <div className="detail-item">
              <strong>Ver en Mapa:</strong>{' '}
              <a href={ensureScheme(library.location_link)} target="_blank" rel="noopener noreferrer">
                Google Maps
              </a>
            </div>
          )}
        </div>

        {/* Horarios Detallados por Día */}
        {library.detailed_schedule && Object.keys(library.detailed_schedule).length > 0 && (
          <div className="detail-section detail-section-full">
            <h3>🕐 Horarios de Operación</h3>
            <div className="schedule-table">
              {Object.entries(dayNames).map(([dayKey, dayName]) => {
                const schedule = library.detailed_schedule[dayKey];
                if (!schedule) return null;
                
                return (
                  <div key={dayKey} className="schedule-row">
                    <div className="schedule-day-name">{dayName}</div>
                    <div className="schedule-hours">
                      {schedule.closed ? (
                        <span className="schedule-closed">CERRADO</span>
                      ) : (
                        <span className="schedule-open">
                          {formatTime12h(schedule.open)} - {formatTime12h(schedule.close)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {library.services && library.services.length > 0 && (
          <div className="detail-section">
            <h3>✨ Servicios</h3>
            <ul className="services-list">
              {library.services.map((service, index) => (
                <li key={index}>{service}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Servicios Gubernamentales */}
        {hasValue(library.government_services) && (
          <div className="detail-section">
            <h3>🏛️ Servicios Gubernamentales</h3>
            <p>{library.government_services}</p>
          </div>
        )}

        {/* Otros Servicios */}
        {hasValue(library.other_services) && (
          <div className="detail-section">
            <h3>➕ Otros Servicios</h3>
            <p>{cleanDisplay(library.other_services)}</p>
          </div>
        )}

        {library.resources && library.resources.length > 0 && (
          <div className="detail-section">
            <h3>📚 Recursos</h3>
            <ul className="resources-list">
              {library.resources.map((resource, index) => (
                <li key={index}>{resource}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Nueva Sección: Equipo Audiovisual */}
        {hasValue(library.audiovisual_equipment) && (
          <div className="detail-section">
            <h3>🎬 Equipo Audiovisual</h3>
            <p>{library.audiovisual_equipment}</p>
          </div>
        )}

        {/* Nueva Sección: Equipos para Préstamo */}
        {hasValue(library.lending_equipment) && (
          <div className="detail-section">
            <h3>💻 Equipos Disponibles</h3>
            <p><strong>Préstamo o uso en sala:</strong> {library.lending_equipment}</p>
          </div>
        )}

        {/* Nueva Sección: Recursos Especiales */}
        {hasValue(library.special_resources) && (
          <div className="detail-section">
            <h3>⭐ Recursos Especiales</h3>
            <p>{library.special_resources}</p>
          </div>
        )}

        {/* Colección */}
        {(hasValue(library.collection_size) || hasValue(library.has_catalog) || hasValue(library.special_collections) || hasValue(library.other_special_collections)) && (
          <div className="detail-section">
            <h3>📖 Colección</h3>
            {hasValue(library.collection_size) && (
              <div className="detail-item">
                <strong>Tamaño de la Colección:</strong> {library.collection_size}
              </div>
            )}
            {hasValue(library.has_catalog) && (
              <div className="detail-item">
                <strong>Tiene Catálogo:</strong> {library.has_catalog}
              </div>
            )}
            {hasValue(library.special_collections) && (
              <div className="detail-item">
                <strong>Colecciones Especiales/Raras:</strong> {library.special_collections}
              </div>
            )}
            {hasValue(library.other_special_collections) && (
              <div className="detail-item">
                <strong>Otras Colecciones:</strong> {library.other_special_collections}
              </div>
            )}
          </div>
        )}

        {/* Accesibilidad Mejorada */}
        {(hasValue(library.accessibility) || hasValue(library.accessibility_resources)) && (
          <div className="detail-section">
            <h3>♿ Accesibilidad</h3>
            {hasValue(library.accessibility) && (
              <div className="detail-item">
                <strong>Prácticas de Accesibilidad:</strong>
                <p>{library.accessibility.split(/\s+/).map((word, i) => {
                  if (isValidUrl(word)) {
                    return <span key={i}><a href={ensureScheme(word)} target="_blank" rel="noopener noreferrer">{word}</a> </span>;
                  }
                  return word + ' ';
                })}</p>
              </div>
            )}
            {hasValue(library.accessibility_resources) && (
              <div className="detail-item">
                <strong>Recursos para Diversidad Funcional:</strong>
                <p>{library.accessibility_resources.split(/\s+/).map((word, i) => {
                  if (isValidUrl(word)) {
                    return <span key={i}><a href={ensureScheme(word)} target="_blank" rel="noopener noreferrer">{word}</a> </span>;
                  }
                  return word + ' ';
                })}</p>
              </div>
            )}
          </div>
        )}

        {/* Personal */}
        {(library.staff_count || library.volunteer_count) && (
          <div className="detail-section">
            <h3>👥 Personal</h3>
            {library.staff_count > 0 && (
              <div className="detail-item">
                <strong>Empleados:</strong> {library.staff_count}
              </div>
            )}
            {library.volunteer_count > 0 && (
              <div className="detail-item">
                <strong>Voluntarios:</strong> {library.volunteer_count}
              </div>
            )}
          </div>
        )}

        {/* Redes Sociales */}
        {library.social_media && Object.keys(library.social_media).length > 0 && (
          <div className="detail-section">
            <h3>📱 Redes Sociales</h3>
            {Object.entries(library.social_media).map(([platform, url]) => {
              if (!url || url === 'N/A' || url.toUpperCase() === 'NO') return null;
              const validUrl = isValidUrl(url);
              return (
                <div key={platform} className="detail-item">
                  <strong>{platform.charAt(0).toUpperCase() + platform.slice(1)}:</strong>{' '}
                  {validUrl ? (
                    <a href={ensureScheme(url)} target="_blank" rel="noopener noreferrer">{url}</a>
                  ) : (
                    <span>{url}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Tour Virtual — only rendered when a valid URL exists */}
        {library.virtual_tour && isValidUrl(library.virtual_tour) && (
          <div className="detail-section">
            <h3>Tour Virtual</h3>
            <div className="detail-item">
              <a
                href={library.virtual_tour}
                target="_blank"
                rel="noopener noreferrer"
                className="virtual-tour-link"
                data-testid="virtual-tour-link"
                aria-label={`Explorar biblioteca (abre el tour virtual de ${library.official_name} en una pestaña nueva)`}
              >
                Explorar biblioteca
              </a>
            </div>
          </div>
        )}

        {/* Información Institucional */}
        {(hasValue(library.part_of_network) || hasValue(library.network_name) || hasValue(library.incorporation_status)) && (
          <div className="detail-section">
            <h3>🏢 Información Institucional</h3>
            {hasValue(library.part_of_network) && (
              <div className="detail-item">
                <strong>Parte de un Sistema/Red:</strong> {library.part_of_network}
              </div>
            )}
            {hasValue(library.network_name) && (
              <div className="detail-item">
                <strong>Red a la que Pertenece:</strong> {library.network_name}
              </div>
            )}
            {hasValue(library.incorporation_status) && (
              <div className="detail-item">
                <strong>Tipo de Incorporación:</strong> {library.incorporation_status}
              </div>
            )}
          </div>
        )}

        {/* Rol en la Comunidad */}
        {hasValue(library.community_role) && (
          <div className="detail-section detail-section-full">
            <h3>🌟 Rol en la Comunidad</h3>
            <p>{library.community_role}</p>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
  };

  // About Page Component
  const AboutPage = () => {
    return (
      <div className="about-page">
        <div className="about-header">
          <button 
            className="btn-back"
            onClick={() => setViewMode('list')}
          >
            ← Volver a la Lista
          </button>
          <h1 className="about-title">Sobre el Directorio</h1>
        </div>

        <div className="about-content">
          {/* Informative Text Section */}
          <div className="about-info">
            <div className="about-text">
              <p>
                El Directorio Nacional de Bibliotecas de Puerto Rico es una iniciativa de LWB US y organizaciones aliadas creada para mapear, documentar y visibilizar todos los espacios de acceso a educación, alfabetización, información, tecnología y cultura existentes en todo el archipiélago. Este proyecto surge de la necesidad urgente de contar con una fuente centralizada, confiable y actualizada que refleje el estado real de las bibliotecas públicas, académicas, comunitarias y especializadas, así como de otros espacios que ofrecen servicios y recursos con el potencial de transformar vidas.
              </p>
              
              <p>
                Inspirado por años de trabajo de campo, alianzas con bibliotecarias, organizaciones comunitarias y entidades culturales, el Directorio responde a una realidad ampliamente reconocida: la falta de datos consolidados limita la capacidad de las comunidades, los municipios y las instituciones para fortalecer estos espacios y recursos esenciales.
              </p>
              
              <p>
                Hoy, el Directorio cuenta con un listado comprensivo y detallado de bibliotecas y un mapa interactivo de libre acceso que permite a cualquier persona encontrar recursos y servicios bibliotecarios según su ubicación y sus necesidades. Esta primera fase también sienta las bases para esfuerzos futuros dirigidos a revitalizar y evolucionar las bibliotecas, lanzar proyectos pilotos, fomentar alianzas duraderas y expandir el acceso equitativo a recursos de aprendizaje en todo el archipiélago.
              </p>
            </div>
          </div>

          {/* Organizaciones Aliadas Section */}
          <div className="about-collaborators">
            <h2 className="about-section-title">Organizaciones Aliadas</h2>
            
            <div className="collaborators-logos-about">
              <a 
                href="https://www.librarieswithoutborders.us/welcome/lwb-library-directory-web-page/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="logo-item-about"
              >
                <img 
                  src="/logo_lwb.png" 
                  alt="Libraries Without Borders US" 
                  className="collaborator-logo-about"
                />
                <p className="logo-caption-about">Libraries Without Borders US</p>
              </a>
              
              <a 
                href="https://cobimet.org/cbm/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="logo-item-about"
              >
                <img 
                  src="/logo_cobimet.png" 
                  alt="Consorcio de Bibliotecas Metropolitanas" 
                  className="collaborator-logo-about"
                />
                <p className="logo-caption-about">Consorcio de Bibliotecas Metropolitanas (COBIMET)</p>
              </a>
              
              <a 
                href="https://sociedadbibliotecariospr.wordpress.com/?fbclid=IwY2xjawOZudBleHRuA2FlbQIxMABicmlkETF3NVZQeFFJS3dpa1diRGNzc3J0YwZhcHBfaWQQMjIyMDM5MTc4ODIwMDg5MgABHpX8JtaJjBHqwwvuLz86UbkcmIoWdTx5b5Wz4SHKcY36-gqdwjgcJ_2NZK1g_aem_g_5c-tLvPfwxLd72GIEI5w" 
                target="_blank" 
                rel="noopener noreferrer"
                className="logo-item-about"
              >
                <img 
                  src="/logo_sbpr.png" 
                  alt="Sociedad de Bibliotecarios de Puerto Rico" 
                  className="collaborator-logo-about"
                />
                <p className="logo-caption-about">Sociedad de Bibliotecarios de Puerto Rico (SBPR)</p>
              </a>
              
              <a 
                href="https://www.cencorpr.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="logo-item-about"
              >
                <img 
                  src="/logo_cencor.png" 
                  alt="Centro de Conservación y Restauración de Puerto Rico" 
                  className="collaborator-logo-about"
                />
                <p className="logo-caption-about">Centro de Conservación y Restauración de Puerto Rico (CENCOR)</p>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <div className="logo-container">
            <img 
              src="/LWB_Logo.png" 
              alt="Directorio Nacional de Bibliotecas de Puerto Rico" 
              className="app-logo"
            />
          </div>
          <h1 className="app-title">
            Directorio Nacional de Bibliotecas de Puerto Rico
          </h1>
          <p className="app-subtitle">Encuentra bibliotecas en todo el archipiélago</p>
          <div className="library-counter">
            <span className="counter-text">
              📚 {libraries.length} bibliotecas en el directorio
            </span>
          </div>
        </div>
        
        <div className="header-controls">
          {/* CRM Controls - Only show when authenticated */}
          {isAuthenticated && (
            <div className="crm-controls">
              <div className="user-controls">
                <span className="user-welcome">
                  Hola, {user.library_name}
                </span>
                <button onClick={() => setViewMode('dashboard')} className="crm-button">
                  <UserCog size={14} />
                  Mi Biblioteca
                </button>
                {user?.is_admin && (
                  <button onClick={() => setViewMode('admin')} className="admin-button">
                    <Shield size={14} />
                    Admin Maestro
                  </button>
                )}
                <button onClick={() => { logout(); setViewMode('list'); }} className="btn-secondary">
                  <LogOut size={14} />
                  Salir
                </button>
              </div>
            </div>
          )}
          
          <div className="search-container">
            <input
              type="text"
              placeholder="Buscar por nombre de biblioteca o nombre conocido..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
              className="search-input"
              data-testid="search-input"
            />
          </div>
          
          <div className="filter-controls">
            <select
              value={filters.municipality}
              onChange={(e) => {
                const newValue = e.target.value;
                console.log('Municipality filter changed to:', newValue);
                setFilters(prev => ({ ...prev, municipality: newValue }));
              }}
              className="filter-select"
            >
              <option value="">Todos los Municipios</option>
              {municipalities.map(municipality => (
                <option key={municipality} value={municipality}>{municipality}</option>
              ))}
            </select>
            
            <select
              value={filters.library_type}
              onChange={(e) => {
                const newValue = e.target.value;
                console.log('Library type filter changed to:', newValue);
                setFilters(prev => ({ ...prev, library_type: newValue }));
              }}
              className="filter-select"
            >
              <option value="">Todos los Tipos</option>
              {libraryTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <div className="view-controls">
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List size={20} />
              Lista
            </button>
            <button
              className={`view-btn ${viewMode === 'map' ? 'active' : ''}`}
              onClick={() => setViewMode('map')}
            >
              <Map size={20} />
              Mapa
            </button>
            <button
              className={`view-btn ${viewMode === 'about' ? 'active' : ''}`}
              onClick={() => setViewMode('about')}
            >
              <Info size={20} />
              Conoce más
            </button>
          </div>
        </div>
      </header>

      <main className="app-main">
        {viewMode === 'list' && (
          <div className="libraries-list">
            {loading ? (
              <div className="loading">Cargando bibliotecas...</div>
            ) : libraries.length === 0 ? (
              <div className="no-results">
                No se encontraron bibliotecas con los filtros seleccionados.
              </div>
            ) : (
              <div className="libraries-grid">
                {libraries.map(library => (
                  <LibraryCard key={library.id} library={library} />
                ))}
              </div>
            )}
          </div>
        )}

        {viewMode === 'map' && (
          <MapView 
            libraries={getFilteredMapLibraries()}
            onLibrarySelect={(library) => {
              // Find the corresponding library in our main library list
              const matchedLibrary = libraries.find(lib => 
                lib.id === library.id || 
                lib.official_name === library.official_name ||
                lib.official_name === library.name
              ) || library;
              
              openModal(matchedLibrary);
            }}
            selectedLibrary={selectedLibrary}
          />
        )}

        {viewMode === 'about' && (
          <AboutPage />
        )}

        {viewMode === 'login' && (
          <Login onBack={() => setViewMode('list')} />
        )}

        {viewMode === 'change-password' && isAuthenticated && mustChangePassword && (
          <ForcePasswordChange />
        )}

        {viewMode === 'dashboard' && isAuthenticated && !mustChangePassword && (
          <LibraryDashboard onBack={() => setViewMode('list')} />
        )}

        {viewMode === 'admin' && isAuthenticated && user?.is_admin && !mustChangePassword && (
          <AdminDashboard onBack={() => setViewMode('list')} />
        )}
      </main>

      {/* Library Detail Modal - Rendered via Portal */}
      {showModal && selectedLibrary && ReactDOM.createPortal(
        <LibraryDetailModal library={selectedLibrary} onClose={closeModal} />,
        document.body
      )}

      {/* Collaborators Section - Only show on list and map views */}
      {['list', 'map'].includes(viewMode) && (
        <footer className="collaborators-section">
          <div className="collaborators-container">
            <h3 className="collaborators-title">Organizaciones Aliadas</h3>
            <p className="collaborators-text">
              El Directorio Nacional de Bibliotecas de Puerto Rico es posible gracias al apoyo y 
              colaboración de las siguientes organizaciones comprometidas con el fortalecimiento de 
              las bibliotecas y el acceso a la información en Puerto Rico.
            </p>
            
            <div className="collaborators-logos">
              <a 
                href="https://www.librarieswithoutborders.us/welcome/lwb-library-directory-web-page/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="logo-item logo-link"
              >
                <img 
                  src="/logo_lwb.png" 
                  alt="Libraries Without Borders US" 
                  className="collaborator-logo"
                />
                <p className="logo-caption">Libraries Without Borders US</p>
              </a>
              
              <a 
                href="https://cobimet.org/cbm/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="logo-item logo-link"
              >
                <img 
                  src="/logo_cobimet.png" 
                  alt="Consorcio de Bibliotecas Metropolitanas" 
                  className="collaborator-logo"
                />
                <p className="logo-caption">Consorcio de Bibliotecas Metropolitanas (COBIMET)</p>
              </a>
              
              <a 
                href="https://sociedadbibliotecariospr.wordpress.com/?fbclid=IwY2xjawOZudBleHRuA2FlbQIxMABicmlkETF3NVZQeFFJS3dpa1diRGNzc3J0YwZhcHBfaWQQMjIyMDM5MTc4ODIwMDg5MgABHpX8JtaJjBHqwwvuLz86UbkcmIoWdTx5b5Wz4SHKcY36-gqdwjgcJ_2NZK1g_aem_g_5c-tLvPfwxLd72GIEI5w" 
                target="_blank" 
                rel="noopener noreferrer"
                className="logo-item logo-link"
              >
                <img 
                  src="/logo_sbpr.png" 
                  alt="Sociedad de Bibliotecarios de Puerto Rico" 
                  className="collaborator-logo"
                />
                <p className="logo-caption">Sociedad de Bibliotecarios de Puerto Rico (SBPR)</p>
              </a>
              
              <a 
                href="https://www.cencorpr.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="logo-item logo-link"
              >
                <img 
                  src="/logo_cencor.png" 
                  alt="Centro de Conservación y Restauración de Puerto Rico" 
                  className="collaborator-logo"
                />
                <p className="logo-caption">Centro de Conservación y Restauración de Puerto Rico (CENCOR)</p>
              </a>
            </div>
          </div>
        </footer>
      )}

      {/* Fixed CRM Access Button - Only show when authenticated and on public views */}
      {isAuthenticated && !mustChangePassword && !['login', 'dashboard', 'admin', 'change-password'].includes(viewMode) && (
        <button 
          className="fixed-crm-button"
          onClick={() => setViewMode(user?.is_admin ? 'admin' : 'dashboard')}
          title={user?.is_admin ? 'Panel Admin' : 'Mi Biblioteca'}
        >
          <Shield size={16} />
          {user?.is_admin ? 'Admin Panel' : 'Mi Biblioteca'}
        </button>
      )}

      {/* Fixed Login Button - Only show when NOT authenticated and NOT in login view */}
      {!isAuthenticated && viewMode !== 'login' && (
        <button 
          className="fixed-admin-login-button"
          onClick={() => setViewMode('login')}
          title="Iniciar Sesión"
          data-testid="admin-login-button"
        >
          <UserCog size={16} />
          Iniciar Sesión
        </button>
      )}
    </div>
  );
};

// Wrap MainApp with AuthProvider
const App = () => (
  <AuthProvider>
    <MainApp />
  </AuthProvider>
);

export default App;